<div class="p-6 bg-gray-50 min-h-screen">
  <!-- Page Header -->
  <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-4 border-b border-gray-200">
    <div>
      <div class="flex items-center gap-3">
        <div class="w-11 h-11 bg-gradient-to-tr from-amber-500 to-yellow-400 rounded-xl flex items-center justify-center text-white shadow-md shadow-yellow-200">
          <i class="fas fa-trophy text-xl"></i>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Agent Performance Leaderboard</h1>
          <p class="text-sm text-gray-500">Track agent rankings, collection value, and scrap-to-voucher conversions</p>
        </div>
      </div>
    </div>

    <!-- Actions & Export -->
    <div class="mt-4 md:mt-0 flex items-center gap-3">
      <button (click)="loadLeaderboard()" [disabled]="loading"
        class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-all">
        <i class="fas fa-sync-alt mr-2 text-gray-500" [class.fa-spin]="loading"></i> Refresh
      </button>
      <button (click)="exportCsv()" [disabled]="!filteredAgents.length || loading"
        class="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium shadow-sm transition-all disabled:opacity-50">
        <i class="fas fa-download mr-2"></i> Export CSV
      </button>
    </div>
  </div>

  <!-- Filters Panel -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
    <div class="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
      <!-- Date Presets -->
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-xs font-semibold uppercase text-gray-400 mr-2 tracking-wider">Date Range:</span>
        <button *ngFor="let p of presets" (click)="selectPreset(p.id)"
          [class.bg-green-600]="selectedPreset === p.id"
          [class.text-white]="selectedPreset === p.id"
          [class.bg-gray-100]="selectedPreset !== p.id"
          [class.text-gray-700]="selectedPreset !== p.id"
          [class.hover:bg-gray-200]="selectedPreset !== p.id"
          class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5">
          <i class="fas {{ p.icon }} text-xs" [class.text-white]="selectedPreset === p.id" [class.text-gray-400]="selectedPreset !== p.id"></i>
          {{ p.label }}
        </button>
      </div>

      <!-- Custom Date Pickers & Zone Filter -->
      <div class="flex flex-wrap items-center gap-3">
        <div *ngIf="selectedPreset === 'custom'" class="flex items-center gap-2">
          <div class="flex items-center gap-1.5 bg-gray-50 border border-gray-300 rounded-lg px-2 py-1">
            <span class="text-xs text-gray-500">From:</span>
            <input type="date" [(ngModel)]="fromDate" (change)="onCustomDateChange()"
              class="bg-transparent text-xs text-gray-800 focus:outline-none" />
          </div>
          <div class="flex items-center gap-1.5 bg-gray-50 border border-gray-300 rounded-lg px-2 py-1">
            <span class="text-xs text-gray-500">To:</span>
            <input type="date" [(ngModel)]="toDate" (change)="onCustomDateChange()"
              class="bg-transparent text-xs text-gray-800 focus:outline-none" />
          </div>
        </div>

        <!-- GeoZone Filter -->
        <div class="min-w-[180px]">
          <select [(ngModel)]="selectedZone" (change)="onZoneChange()"
            class="w-full text-xs bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-green-500 focus:outline-none">
            <option value="">All Geo Zones</option>
            <option *ngFor="let z of zones" [value]="z.id">{{ z.name }}</option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-3"></div>
    <p class="text-sm text-gray-500 font-medium">Crunching agent performance &amp; voucher conversion data...</p>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage && !loading" class="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-6">
    <div class="flex items-center">
      <i class="fas fa-exclamation-circle text-red-500 mr-3"></i>
      <p class="text-sm text-red-700">{{ errorMessage }}</p>
    </div>
  </div>

  <ng-container *ngIf="data && !loading">
    <!-- Fleet Summary KPI Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
      <!-- Top Voucher Converter Card -->
      <div class="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-4 text-white shadow-sm flex flex-col justify-between relative overflow-hidden">
        <div class="absolute -right-2 -bottom-2 opacity-15 text-6xl text-white">
          <i class="fas fa-ticket-alt"></i>
        </div>
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-emerald-100 uppercase tracking-wider">Top Voucher Star</span>
          <span class="p-1.5 bg-emerald-500 bg-opacity-30 rounded-lg text-emerald-200">
            <i class="fas fa-award"></i>
          </span>
        </div>
        <div>
          <div class="flex items-center gap-2">
            <app-agent-avatar *ngIf="data.fleetSummary.topVoucherConverterAgentId" [url]="pictureOf(data.fleetSummary.topVoucherConverterAgentId)" [name]="data.fleetSummary.topVoucherConverterAgentName || ''"></app-agent-avatar>
            <div class="text-base font-bold truncate">{{ data.fleetSummary.topVoucherConverterAgentName || 'None' }}</div>
          </div>
          <div class="text-xs text-emerald-100 mt-0.5">
            <span class="font-bold text-white text-sm">{{ data.fleetSummary.topVoucherConversionRate }}%</span> conversion rate
          </div>
        </div>
      </div>

      <!-- Total Scrap Value Collected -->
      <div class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col justify-between">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Scrap Value</span>
          <span class="p-1.5 bg-amber-50 rounded-lg text-amber-600">
            <i class="fas fa-rupee-sign"></i>
          </span>
        </div>
        <div>
          <div class="text-2xl font-bold text-gray-900">₹{{ data.fleetSummary.totalBusinessValue | number:'1.0-0' }}</div>
          <div class="text-xs text-gray-500 mt-1 truncate">
            Top: <app-agent-avatar *ngIf="data.fleetSummary.topVolumeCollectorAgentId" class="inline-block align-middle" [url]="pictureOf(data.fleetSummary.topVolumeCollectorAgentId)" [name]="data.fleetSummary.topVolumeCollectorAgentName || ''" sizeClass="w-5 h-5 text-[8px]"></app-agent-avatar>
            <span class="font-medium text-gray-700">{{ data.fleetSummary.topVolumeCollectorAgentName || 'N/A' }}</span>
          </div>
        </div>
      </div>

      <!-- Voucher Conversion Rate -->
      <div class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col justify-between">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Voucher Conversions</span>
          <span class="p-1.5 bg-indigo-50 rounded-lg text-indigo-600">
            <i class="fas fa-exchange-alt"></i>
          </span>
        </div>
        <div>
          <div class="text-2xl font-bold text-gray-900">{{ data.fleetSummary.fleetVoucherConversionRate }}%</div>
          <div class="text-xs text-gray-500 mt-1">
            <span class="font-medium text-gray-700">{{ data.fleetSummary.totalVoucherPickups }}</span> / {{ data.fleetSummary.totalPickups }} pickups
          </div>
        </div>
      </div>

      <!-- Completed Pickups -->
      <div class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col justify-between">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Completed Pickups</span>
          <span class="p-1.5 bg-blue-50 rounded-lg text-blue-600">
            <i class="fas fa-truck-loading"></i>
          </span>
        </div>
        <div>
          <div class="text-2xl font-bold text-gray-900">{{ data.fleetSummary.totalPickups }}</div>
          <div class="text-xs text-gray-500 mt-1">
            <span class="font-medium text-gray-700">{{ data.fleetSummary.activeAgentsCount }}</span> active agents
          </div>
        </div>
      </div>

      <!-- Total Gross Profit -->
      <div class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col justify-between">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Est. Gross Profit</span>
          <span class="p-1.5 bg-green-50 rounded-lg text-green-600">
            <i class="fas fa-chart-line"></i>
          </span>
        </div>
        <div>
          <div class="text-2xl font-bold text-green-600">+₹{{ data.fleetSummary.totalProfit | number:'1.0-0' }}</div>
          <div class="text-xs text-gray-500 mt-1">
            Market Val: ₹{{ data.fleetSummary.totalMarketValue | number:'1.0-0' }}
          </div>
        </div>
      </div>

      <!-- Fleet Average Rating -->
      <div class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col justify-between">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Fleet Avg Rating</span>
          <span class="p-1.5 bg-yellow-50 rounded-lg text-yellow-500">
            <i class="fas fa-star"></i>
          </span>
        </div>
        <div>
          <div class="text-2xl font-bold text-gray-900 flex items-center gap-1.5">
            {{ data.fleetSummary.averageRating }}
            <div class="flex text-yellow-400 text-xs">
              <i class="fas fa-star" *ngFor="let s of getStarArray(data.fleetSummary.averageRating)"></i>
            </div>
          </div>
          <div class="text-xs text-gray-500 mt-1 truncate">
            Top: <app-agent-avatar *ngIf="data.fleetSummary.topRatedAgentId" class="inline-block align-middle" [url]="pictureOf(data.fleetSummary.topRatedAgentId)" [name]="data.fleetSummary.topRatedAgentName || ''" sizeClass="w-5 h-5 text-[8px]"></app-agent-avatar>
            <span class="font-medium text-gray-700">{{ data.fleetSummary.topRatedAgentName || 'N/A' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Podium Showcase (Top 3 Agents) -->
    <div *ngIf="getTop3Agents().length > 0" class="mb-6">
      <div class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 px-1">Top Performers Podium</div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Loop over Top 3 -->
        <div *ngFor="let agent of getTop3Agents(); let idx = index"
          class="rounded-xl border p-5 relative overflow-hidden transition-all hover:shadow-md cursor-pointer"
          [class.bg-gradient-to-b]="idx === 0"
          [class.from-amber-50]="idx === 0"
          [class.to-white]="idx === 0"
          [class.border-amber-300]="idx === 0"
          [class.shadow-amber-100]="idx === 0"
          [class.bg-white]="idx !== 0"
          [class.border-gray-200]="idx !== 0"
          (click)="openDrilldown(agent)">
          
          <!-- Rank Ribbon Badge -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <span *ngIf="idx === 0" class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-amber-900 font-extrabold text-sm shadow-sm">
                🥇
              </span>
              <span *ngIf="idx === 1" class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-300 text-slate-800 font-extrabold text-sm shadow-sm">
                🥈
              </span>
              <span *ngIf="idx === 2" class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-700 text-amber-100 font-extrabold text-sm shadow-sm">
                🥉
              </span>
              <app-agent-avatar [url]="agent.profilePictureUrl" [name]="agent.agentName" sizeClass="w-12 h-12 text-sm"></app-agent-avatar>
              <div>
                <h3 class="font-bold text-gray-900 leading-tight">{{ agent.agentName }}</h3>
                <span class="text-xs text-gray-500">{{ agent.phoneNumber }}</span>
              </div>
            </div>
            <span class="text-xs font-bold px-2.5 py-1 rounded-full"
              [class.bg-amber-100]="idx === 0" [class.text-amber-800]="idx === 0"
              [class.bg-slate-100]="idx === 1" [class.text-slate-800]="idx === 1"
              [class.bg-orange-100]="idx === 2" [class.text-orange-800]="idx === 2">
              Rank #{{ agent.rank }}
            </span>
          </div>

          <!-- Highlight Badges -->
          <div class="flex flex-wrap gap-1 mb-4">
            <span *ngFor="let badge of agent.badges" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[11px] font-medium">
              {{ badge }}
            </span>
          </div>

          <!-- Metrics Grid in Podium Card -->
          <div class="grid grid-cols-2 gap-2 bg-gray-50 rounded-lg p-3 text-xs mb-3 border border-gray-100">
            <div>
              <span class="text-gray-500 block">Voucher Conv.</span>
              <span class="font-bold text-indigo-700 text-sm">{{ agent.voucherConversionRate }}%</span>
              <span class="text-[10px] text-gray-400 block">({{ agent.voucherCollectionsCount }} / {{ agent.totalCollections }} pickups)</span>
            </div>
            <div>
              <span class="text-gray-500 block">Collection Value</span>
              <span class="font-bold text-amber-700 text-sm">₹{{ agent.totalGrossValue | number:'1.0-0' }}</span>
              <span class="text-[10px] text-green-600 block">+₹{{ agent.totalProfit | number:'1.0-0' }} profit</span>
            </div>
            <div>
              <span class="text-gray-500 block">Completed</span>
              <span class="font-bold text-gray-800 text-sm">{{ agent.totalCollections }} pickups</span>
              <span class="text-[10px] text-gray-400 block">{{ agent.completionRate }}% fulfillment</span>
            </div>
            <div>
              <span class="text-gray-500 block">Rating</span>
              <div class="flex items-center gap-1 font-bold text-gray-800 text-sm">
                <span>{{ agent.averageRating > 0 ? agent.averageRating : 'N/A' }}</span>
                <i class="fas fa-star text-yellow-400 text-xs" *ngIf="agent.averageRating > 0"></i>
              </div>
              <span class="text-[10px] text-gray-400 block">{{ agent.totalRatingsCount }} reviews</span>
            </div>
          </div>

          <div class="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-gray-100">
            <span>Score: <strong class="text-gray-800">{{ agent.compositeScore }}</strong></span>
            <span class="text-green-600 hover:text-green-700 font-medium flex items-center gap-1">
              View details <i class="fas fa-chevron-right text-[10px]"></i>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Tabs & Search Bar -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div class="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <!-- Tabs -->
        <div class="flex flex-wrap gap-2">
          <button (click)="setTab('all')"
            [class.bg-green-600]="activeTab === 'all'" [class.text-white]="activeTab === 'all'"
            [class.bg-gray-100]="activeTab !== 'all'" [class.text-gray-700]="activeTab !== 'all'"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5">
            <i class="fas fa-list-ol"></i> Overall Ranking
          </button>
          <button (click)="setTab('vouchers')"
            [class.bg-indigo-600]="activeTab === 'vouchers'" [class.text-white]="activeTab === 'vouchers'"
            [class.bg-gray-100]="activeTab !== 'vouchers'" [class.text-gray-700]="activeTab !== 'vouchers'"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5">
            <i class="fas fa-ticket-alt"></i> Top Voucher Converters
          </button>
          <button (click)="setTab('value')"
            [class.bg-amber-600]="activeTab === 'value'" [class.text-white]="activeTab === 'value'"
            [class.bg-gray-100]="activeTab !== 'value'" [class.text-gray-700]="activeTab !== 'value'"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5">
            <i class="fas fa-coins"></i> Collection Value (₹)
          </button>
          <button (click)="setTab('pickups')"
            [class.bg-blue-600]="activeTab === 'pickups'" [class.text-white]="activeTab === 'pickups'"
            [class.bg-gray-100]="activeTab !== 'pickups'" [class.text-gray-700]="activeTab !== 'pickups'"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5">
            <i class="fas fa-truck-pickup"></i> Most Pickups
          </button>
          <button (click)="setTab('rating')"
            [class.bg-yellow-500]="activeTab === 'rating'" [class.text-white]="activeTab === 'rating'"
            [class.bg-gray-100]="activeTab !== 'rating'" [class.text-gray-700]="activeTab !== 'rating'"
            class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5">
            <i class="fas fa-star"></i> Highest Rated
          </button>
        </div>

        <!-- Search box -->
        <div class="relative min-w-[240px]">
          <input type="text" [(ngModel)]="searchQuery" (input)="onSearchChange()"
            placeholder="Search agent name or phone..."
            class="w-full text-xs pl-8 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:ring-2 focus:ring-green-500 focus:outline-none" />
          <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-xs"></i>
          <button *ngIf="searchQuery" (click)="searchQuery = ''; onSearchChange()" class="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Leaderboard Data Table -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" (click)="sortBy('rank')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Rank <i class="fas" [class.fa-sort-up]="sortColumn === 'rank' && sortAscending" [class.fa-sort-down]="sortColumn === 'rank' && !sortAscending" [class.fa-sort]="sortColumn !== 'rank'"></i>
              </th>
              <th scope="col" (click)="sortBy('agentName')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Agent <i class="fas" [class.fa-sort-up]="sortColumn === 'agentName' && sortAscending" [class.fa-sort-down]="sortColumn === 'agentName' && !sortAscending" [class.fa-sort]="sortColumn !== 'agentName'"></i>
              </th>
              <th scope="col" (click)="sortBy('voucherConversionRate')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Voucher Conversion <i class="fas" [class.fa-sort-up]="sortColumn === 'voucherConversionRate' && sortAscending" [class.fa-sort-down]="sortColumn === 'voucherConversionRate' && !sortAscending" [class.fa-sort]="sortColumn !== 'voucherConversionRate'"></i>
              </th>
              <th scope="col" (click)="sortBy('totalCollections')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Pickups <i class="fas" [class.fa-sort-up]="sortColumn === 'totalCollections' && sortAscending" [class.fa-sort-down]="sortColumn === 'totalCollections' && !sortAscending" [class.fa-sort]="sortColumn !== 'totalCollections'"></i>
              </th>
              <th scope="col" (click)="sortBy('totalGrossValue')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Collection Value (₹) <i class="fas" [class.fa-sort-up]="sortColumn === 'totalGrossValue' && sortAscending" [class.fa-sort-down]="sortColumn === 'totalGrossValue' && !sortAscending" [class.fa-sort]="sortColumn !== 'totalGrossValue'"></i>
              </th>
              <th scope="col" (click)="sortBy('totalProfit')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Gross Profit (₹) <i class="fas" [class.fa-sort-up]="sortColumn === 'totalProfit' && sortAscending" [class.fa-sort-down]="sortColumn === 'totalProfit' && !sortAscending" [class.fa-sort]="sortColumn !== 'totalProfit'"></i>
              </th>
              <th scope="col" (click)="sortBy('averageRating')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Rating <i class="fas" [class.fa-sort-up]="sortColumn === 'averageRating' && sortAscending" [class.fa-sort-down]="sortColumn === 'averageRating' && !sortAscending" [class.fa-sort]="sortColumn !== 'averageRating'"></i>
              </th>
              <th scope="col" (click)="sortBy('checkInDaysCount')" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                Check-ins / Leaves <i class="fas" [class.fa-sort-up]="sortColumn === 'checkInDaysCount' && sortAscending" [class.fa-sort-down]="sortColumn === 'checkInDaysCount' && !sortAscending" [class.fa-sort]="sortColumn !== 'checkInDaysCount'"></i>
              </th>
              <th scope="col" class="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200 text-xs">
            <tr *ngFor="let agent of filteredAgents" class="hover:bg-gray-50 transition-colors">
              <!-- Rank -->
              <td class="px-4 py-3 whitespace-nowrap">
                <span class="inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs"
                  [class.bg-amber-400]="agent.rank === 1" [class.text-amber-900]="agent.rank === 1"
                  [class.bg-slate-300]="agent.rank === 2" [class.text-slate-800]="agent.rank === 2"
                  [class.bg-amber-700]="agent.rank === 3" [class.text-amber-100]="agent.rank === 3"
                  [class.bg-gray-100]="agent.rank > 3" [class.text-gray-600]="agent.rank > 3">
                  #{{ agent.rank }}
                </span>
              </td>

              <!-- Agent Details -->
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="flex items-center">
                  <app-agent-avatar class="mr-3" [url]="agent.profilePictureUrl" [name]="agent.agentName"></app-agent-avatar>
                  <div>
                    <div class="font-semibold text-gray-900 flex items-center gap-1.5">
                      {{ agent.agentName }}
                      <span *ngIf="agent.voucherConversionRate >= 60 && agent.voucherCollectionsCount >= 3" class="text-amber-500 text-[11px]" title="Voucher Champion">
                        <i class="fas fa-crown"></i>
                      </span>
                    </div>
                    <div class="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                      <span><i class="fas fa-phone text-[9px] mr-1"></i>{{ agent.phoneNumber }}</span>
                      <span *ngIf="agent.vehicleNumber" class="text-gray-400">| {{ agent.vehicleNumber }}</span>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Voucher Conversion -->
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="w-36">
                  <div class="flex items-center justify-between text-xs mb-1">
                    <span class="font-bold text-indigo-700">{{ agent.voucherConversionRate }}%</span>
                    <span class="text-[11px] text-gray-500">{{ agent.voucherCollectionsCount }} / {{ agent.totalCollections }}</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                    <div class="bg-indigo-600 h-1.5 rounded-full" [style.width.%]="agent.voucherConversionRate"></div>
                  </div>
                  <div class="text-[10px] text-gray-400 mt-0.5">
                    ₹{{ agent.totalVoucherValue | number:'1.0-0' }} converted
                  </div>
                </div>
              </td>

              <!-- Pickups & Completion -->
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="font-semibold text-gray-900">{{ agent.totalCollections }} pickups</div>
                <div class="text-[11px] text-gray-500">{{ agent.completionRate }}% fulfillment</div>
              </td>

              <!-- Collection Value -->
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="font-bold text-gray-900">₹{{ agent.totalGrossValue | number:'1.0-0' }}</div>
                <div class="text-[10px] text-gray-500">market: ₹{{ agent.totalMarketValue | number:'1.0-0' }}</div>
              </td>

              <!-- Gross Profit & Commission -->
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="font-semibold text-green-600">+₹{{ agent.totalProfit | number:'1.0-0' }}</div>
                <div class="text-[10px] text-gray-400">comm: ₹{{ agent.totalAgentVoucherCommission | number:'1.0-0' }}</div>
              </td>

              <!-- Rating -->
              <td class="px-4 py-3 whitespace-nowrap">
                <div *ngIf="agent.averageRating > 0" class="flex items-center gap-1">
                  <span class="font-bold text-gray-800">{{ agent.averageRating }}</span>
                  <div class="flex text-yellow-400 text-[10px]">
                    <i class="fas fa-star" *ngFor="let s of getStarArray(agent.averageRating)"></i>
                  </div>
                  <span class="text-[10px] text-gray-400">({{ agent.totalRatingsCount }})</span>
                </div>
                <div *ngIf="agent.averageRating === 0" class="text-gray-400 text-xs">
                  No ratings
                </div>
              </td>

              <!-- Check-ins -->
              <td class="px-4 py-3 whitespace-nowrap">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                  <i class="fas fa-camera mr-1 text-[10px]"></i> {{ agent.checkInDaysCount }} days
                </span>
                <div *ngIf="agent.leaveDays || agent.unplannedLeaveDays" class="text-[11px] text-gray-500 mt-0.5">{{ agent.leaveDays }} leave · {{ agent.unplannedLeaveDays }} unplanned</div>
              </td>

              <!-- Action -->
              <td class="px-4 py-3 whitespace-nowrap text-right text-xs font-medium">
                <button (click)="openDrilldown(agent)"
                  class="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-2.5 py-1 rounded-lg transition-colors">
                  <i class="fas fa-eye mr-1"></i> Details
                </button>
              </td>
            </tr>

            <tr *ngIf="filteredAgents.length === 0">
              <td colspan="9" class="px-4 py-12 text-center text-gray-500">
                <i class="fas fa-inbox text-3xl text-gray-300 mb-2 block"></i>
                No agent activity found matching the selected filter criteria.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ng-container>

  <!-- Agent Drilldown Modal Drawer -->
  <div *ngIf="isModalOpen && selectedAgent" class="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto transform transition-all p-6">
      
      <!-- Modal Header -->
      <div class="flex items-start justify-between border-b border-gray-200 pb-4 mb-5">
        <div class="flex items-center gap-3">
          <app-agent-avatar [url]="selectedAgent.profilePictureUrl" [name]="selectedAgent.agentName" sizeClass="w-12 h-12 text-lg" shapeClass="rounded-xl shadow-md"></app-agent-avatar>
          <div>
            <div class="flex items-center gap-2">
              <h2 class="text-xl font-bold text-gray-900">{{ selectedAgent.agentName }}</h2>
              <span class="px-2.5 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded-full">
                Rank #{{ selectedAgent.rank }}
              </span>
            </div>
            <p class="text-xs text-gray-500 mt-0.5">
              Phone: {{ selectedAgent.phoneNumber }} | Vehicle: {{ selectedAgent.vehicleNumber || 'N/A' }}
            </p>
          </div>
        </div>
        <button (click)="closeModal()" class="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100">
          <i class="fas fa-times text-lg"></i>
        </button>
      </div>

      <!-- Quick KPI Stats Bar -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div class="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
          <span class="text-[11px] font-semibold text-indigo-600 uppercase tracking-wider block">Voucher Conv.</span>
          <span class="text-xl font-bold text-indigo-900">{{ selectedAgent.voucherConversionRate }}%</span>
          <span class="text-[10px] text-indigo-700 block">{{ selectedAgent.voucherCollectionsCount }} of {{ selectedAgent.totalCollections }} pickups</span>
        </div>
        <div class="bg-amber-50 border border-amber-100 rounded-xl p-3">
          <span class="text-[11px] font-semibold text-amber-600 uppercase tracking-wider block">Collection Value</span>
          <span class="text-xl font-bold text-amber-900">₹{{ selectedAgent.totalGrossValue | number:'1.0-0' }}</span>
          <span class="text-[10px] text-amber-700 block">₹{{ selectedAgent.totalMarketValue | number:'1.0-0' }} market val</span>
        </div>
        <div class="bg-green-50 border border-green-100 rounded-xl p-3">
          <span class="text-[11px] font-semibold text-green-600 uppercase tracking-wider block">Profit Margin</span>
          <span class="text-xl font-bold text-green-900">{{ selectedAgent.profitMarginPercentage }}%</span>
          <span class="text-[10px] text-green-700 block">₹{{ selectedAgent.totalProfit | number:'1.0-0' }} gross profit</span>
        </div>
        <div class="bg-yellow-50 border border-yellow-100 rounded-xl p-3">
          <span class="text-[11px] font-semibold text-yellow-600 uppercase tracking-wider block">Avg Rating</span>
          <span class="text-xl font-bold text-yellow-900">{{ selectedAgent.averageRating > 0 ? selectedAgent.averageRating : 'N/A' }}</span>
          <span class="text-[10px] text-yellow-700 block">{{ selectedAgent.totalRatingsCount }} customer reviews</span>
        </div>
      </div>

      <app-agent-score-breakdown [agent]="selectedAgent"></app-agent-score-breakdown>

      <!-- Financial & Voucher Breakdown -->
      <div class="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-6">
        <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
          <i class="fas fa-receipt text-indigo-600"></i> Financial &amp; Voucher Summary
        </h4>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
          <div>
            <span class="text-gray-500 block">Total Voucher Value:</span>
            <span class="font-bold text-gray-900 text-sm">₹{{ selectedAgent.totalVoucherValue | number:'1.0-0' }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Platform Voucher Comm.:</span>
            <span class="font-bold text-gray-900 text-sm">₹{{ selectedAgent.totalVoucherCommission | number:'1.0-0' }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Agent Voucher Comm.:</span>
            <span class="font-bold text-green-600 text-sm">₹{{ selectedAgent.totalAgentVoucherCommission | number:'1.0-0' }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Total Market Value:</span>
            <span class="font-bold text-gray-900 text-sm">₹{{ selectedAgent.totalMarketValue | number:'1.0-0' }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Collection Charges:</span>
            <span class="font-bold text-gray-900 text-sm">₹{{ selectedAgent.totalCollectionCharges | number:'1.0-0' }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Platform Charges:</span>
            <span class="font-bold text-gray-900 text-sm">₹{{ selectedAgent.totalPlatformCharges | number:'1.0-0' }}</span>
          </div>
        </div>
      </div>

      <!-- Top Collected Scrap Items Breakdown Table -->
      <div class="mb-6">
        <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
          <i class="fas fa-boxes text-amber-600"></i> Top Scrap Items Collected
        </h4>
        <div class="overflow-x-auto border border-gray-200 rounded-xl">
          <table class="min-w-full divide-y divide-gray-200 text-xs">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-2 text-left font-semibold text-gray-600">Item</th>
                <th class="px-3 py-2 text-right font-semibold text-gray-600">Net Quantity</th>
                <th class="px-3 py-2 text-right font-semibold text-gray-600">Paid Value</th>
                <th class="px-3 py-2 text-right font-semibold text-gray-600">Market Value</th>
                <th class="px-3 py-2 text-right font-semibold text-gray-600">Profit</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 bg-white">
              <tr *ngFor="let item of selectedAgent.topScrapItems">
                <td class="px-3 py-2 font-medium text-gray-900">{{ item.scrapItemName }}</td>
                <td class="px-3 py-2 text-right font-bold text-gray-800">{{ item.netQuantity | number:'1.0-1' }} {{ item.unit }}</td>
                <td class="px-3 py-2 text-right text-gray-700">₹{{ item.totalValue | number:'1.0-0' }}</td>
                <td class="px-3 py-2 text-right text-gray-700">₹{{ item.totalMarketValue | number:'1.0-0' }}</td>
                <td class="px-3 py-2 text-right font-semibold text-green-600">+₹{{ item.profitValue | number:'1.0-0' }}</td>
              </tr>
              <tr *ngIf="!selectedAgent.topScrapItems || selectedAgent.topScrapItems.length === 0">
                <td colspan="5" class="px-3 py-4 text-center text-gray-400">No item details available.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Customer Feedback Feed -->
      <div *ngIf="selectedAgent.customerFeedbacks && selectedAgent.customerFeedbacks.length > 0" class="mb-4">
        <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
          <i class="fas fa-comments text-blue-600"></i> Recent Customer Feedback
        </h4>
        <div class="space-y-2">
          <div *ngFor="let comment of selectedAgent.customerFeedbacks" class="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-xs text-gray-700 flex items-start gap-2">
            <i class="fas fa-quote-left text-gray-400 text-xs mt-0.5"></i>
            <span>"{{ comment }}"</span>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="flex justify-end pt-4 border-t border-gray-200">
        <button (click)="closeModal()" class="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm transition-colors">
          Close
        </button>
      </div>

    </div>
  </div>
</div>
